/**
 * Pomodoro Menu Sections — accordion panels for the hamburger drawer.
 * Split from pomodoro-menu.tsx to keep files under 300 lines.
 */

import React, { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronDown, Plus, Trash2, Circle, CheckCircle2,
  Clock, Coffee, Sofa, Hash, Timer, Swords,
  Circle as CircleIcon, Square,
} from 'lucide-react'
import { format } from 'date-fns'
import { usePomodoroStore } from '@turnifly/shared/stores'
import { usePomodoroSettings, usePomodoroTasks } from '@turnifly/shared/hooks'
import { useMonthlyHabits } from '@turnifly/shared/hooks'
import { useAuthStore } from '@turnifly/shared/stores'
import { habitAppliesToDate } from '@turnifly/shared/utils'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'
import type { MonthlyHabitItem, PomodoroSettings } from '@turnifly/shared/types'

// ── Accordion wrapper ──

export const Section: React.FC<{
  title: string; icon: React.ReactNode; defaultOpen?: boolean; children: React.ReactNode
}> = ({ title, icon, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border-b border-white/[0.06] pb-3 mb-2">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 py-3 tap-highlight-transparent group">
        <span className="text-white/40 group-hover:text-white/70 transition-colors">{icon}</span>
        <span className="flex-1 text-left text-[14px] font-semibold text-white/90 group-hover:text-white transition-colors">{title}</span>
        <ChevronDown size={14} className={`text-white/30 group-hover:text-white/60 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="pb-3 animate-in fade-in slide-in-from-top-2 duration-300">{children}</div>}
    </div>
  )
}

// ── Tasks ──

export const TasksSection: React.FC = () => {
  const { t } = useTranslation()
  const { tasks, addTask, toggle, remove, clearCompleted } = usePomodoroTasks()
  const setActiveTask = usePomodoroStore((s) => s.setActiveTask)
  const activeTaskId = usePomodoroStore((s) => s.activeTaskId)
  const [text, setText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const completed = tasks.filter((x) => x.completed).length

  const handleAdd = (): void => {
    const v = text.trim()
    if (!v) return
    addTask(v)
    setText('')
  }

  return (
    <Section title={t('pomodoro.tasks')} icon={<CheckCircle2 size={15} />} defaultOpen>
      <div className="space-y-1.5">
        {tasks.map((task) => (
          <div key={task.id} className={`flex items-center gap-3 px-3 py-2 rounded-[12px] transition-all duration-200 ${activeTaskId === task.id ? 'bg-primary/20 ring-1 ring-primary/30' : 'hover:bg-white/[0.05] bg-white/[0.02]'}`}>
            <button onClick={() => { hapticImpact('light'); toggle(task.id, !task.completed) }} className="shrink-0 tap-highlight-transparent transition-transform active:scale-90">
              {task.completed ? <CheckCircle2 size={16} className="text-primary drop-shadow-[0_2px_8px_rgba(var(--color-primary),0.5)]" /> : <Circle size={16} className="text-white/30" />}
            </button>
            <button onClick={() => setActiveTask(activeTaskId === task.id ? null : task.id, task.text)}
              className={`flex-1 text-left text-[13px] truncate tap-highlight-transparent transition-colors ${task.completed ? 'line-through text-white/30' : 'text-white/80'}`}>
              {task.text}
            </button>
            <button onClick={() => remove(task.id)} className="tap-highlight-transparent opacity-0 text-white/30 hover:text-red-400 focus:opacity-100 transition-opacity [div:hover_&]:opacity-100 p-1">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        <div className="flex items-center gap-3 px-3 py-1.5 mt-2 bg-white/[0.03] rounded-[12px] border border-white/[0.05]">
          <Plus size={14} className="text-white/40 shrink-0" />
          <input ref={inputRef} value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()} placeholder={t('pomodoro.addTask')}
            className="flex-1 bg-transparent text-[13px] text-white/90 placeholder:text-white/30 outline-none py-1" maxLength={200} />
        </div>
        {completed > 0 && (
          <button onClick={clearCompleted} className="text-[10px] text-white/20 px-2 tap-highlight-transparent">{t('pomodoro.clearCompleted')}</button>
        )}
      </div>
    </Section>
  )
}

// ── Habits ──

export const HabitsSection: React.FC = () => {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const activeHabitId = usePomodoroStore((s) => s.activeHabitId)
  const setActiveHabit = usePomodoroStore((s) => s.setActiveHabit)
  const updateSettings = usePomodoroStore((s) => s.updateSettings)
  const now = new Date()
  const todayStr = format(now, 'yyyy-MM-dd')
  const { data } = useMonthlyHabits(user?.id ?? '', now.getFullYear(), now.getMonth() + 1)

  const todayHabits = React.useMemo((): MonthlyHabitItem[] => {
    if (!data?.habits) return []
    return data.habits.filter((h) => {
      if (!habitAppliesToDate(h, todayStr)) return false
      return !h.logs?.some((l) => l.log_date === todayStr && l.completed)
    })
  }, [data, todayStr])

  const select = (h: MonthlyHabitItem): void => {
    if (activeHabitId === h.id) { setActiveHabit(null, null); return }
    setActiveHabit(h.id, `${h.icon} ${h.name}`)
    if (h.time_duration && h.time_duration > 0) updateSettings({ workDuration: h.time_duration })
  }

  if (todayHabits.length === 0) return null

  return (
    <Section title={t('pomodoro.todayHabits')} icon={<Timer size={15} />}>
      <div className="space-y-1.5">
        {todayHabits.map((h) => (
          <button key={h.id} onClick={() => select(h)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-[12px] transition-all duration-200 tap-highlight-transparent ${activeHabitId === h.id ? 'bg-primary/20 ring-1 ring-primary/30 shadow-sm' : 'hover:bg-white/[0.05] bg-white/[0.02]'}`}>
            <span className="text-[15px]">{h.icon}</span>
            <span className={`flex-1 text-left text-[13px] font-medium truncate transition-colors ${activeHabitId === h.id ? 'text-white' : 'text-white/80'}`}>{h.name}</span>
            {(h.time_duration ?? 0) > 0 && <span className="text-[11px] font-semibold text-white/40 flex items-center gap-1"><Clock size={11} />{h.time_duration}m</span>}
            {h.challenge_id && <Swords size={12} className="text-white/30" />}
          </button>
        ))}
      </div>
    </Section>
  )
}

// ── Settings ──

export const SettingsSection: React.FC = () => {
  const { t } = useTranslation()
  const workDuration = usePomodoroStore((s) => s.settings.workDuration)
  const shortBreakDuration = usePomodoroStore((s) => s.settings.shortBreakDuration)
  const longBreakDuration = usePomodoroStore((s) => s.settings.longBreakDuration)
  const sessionsBeforeLongBreak = usePomodoroStore((s) => s.settings.sessionsBeforeLongBreak)
  const timerStyle = usePomodoroStore((s) => s.settings.timerStyle)
  const autoStartBreaks = usePomodoroStore((s) => s.settings.autoStartBreaks)
  const autoStartWork = usePomodoroStore((s) => s.settings.autoStartWork)
  const { saveSettings } = usePomodoroSettings()

  const update = (key: string, val: number | boolean | string): void => {
    usePomodoroStore.getState().updateSettings({ [key]: val })
    const dbKey = key.replace(/([A-Z])/g, '_$1').toLowerCase()
    saveSettings({ [dbKey]: val } as Partial<PomodoroSettings>)
  }

  return (
    <Section title={t('pomodoro.settings')} icon={<Clock size={15} />}>
      <div className="space-y-3">
        <SliderRow icon={<Clock size={14} className="text-red-400" />} label={t('pomodoro.workDuration')}
          value={workDuration} min={1} max={120} disabled={false} onChange={(v) => update('workDuration', v)} />
        <SliderRow icon={<Coffee size={14} className="text-teal-400" />} label={t('pomodoro.shortBreakDuration')}
          value={shortBreakDuration} min={1} max={30} disabled={false} onChange={(v) => update('shortBreakDuration', v)} />
        <SliderRow icon={<Sofa size={14} className="text-blue-400" />} label={t('pomodoro.longBreakDuration')}
          value={longBreakDuration} min={1} max={60} disabled={false} onChange={(v) => update('longBreakDuration', v)} />
        <SliderRow icon={<Hash size={14} className="text-purple-400" />} label={t('pomodoro.sessionsCount')}
          value={sessionsBeforeLongBreak} min={1} max={10} disabled={false} onChange={(v) => update('sessionsBeforeLongBreak', v)} />
        <div className="flex gap-1.5">
          <button onClick={() => update('timerStyle', 'circular')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-semibold transition-colors tap-highlight-transparent ${timerStyle === 'circular' ? 'bg-primary/20 text-primary' : 'bg-white/[0.04] text-text-hint'}`}>
            <CircleIcon size={13} /> {t('pomodoro.circular')}
          </button>
          <button onClick={() => update('timerStyle', 'flat')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-semibold transition-colors tap-highlight-transparent ${timerStyle === 'flat' ? 'bg-primary/20 text-primary' : 'bg-white/[0.04] text-text-hint'}`}>
            <Square size={13} /> {t('pomodoro.flat')}
          </button>
        </div>
        <ToggleRow label={t('pomodoro.autoStartBreaks')} checked={autoStartBreaks} onChange={(v) => update('autoStartBreaks', v)} />
        <ToggleRow label={t('pomodoro.autoStartWork')} checked={autoStartWork} onChange={(v) => update('autoStartWork', v)} />
      </div>
    </Section>
  )
}

// ── Shared sub-components ──

const SliderRow: React.FC<{
  icon: React.ReactNode; label: string; value: number; min: number; max: number; disabled: boolean; onChange: (v: number) => void
}> = ({ icon, label, value, min, max, disabled, onChange }) => (
  <div className={`p-3 rounded-[12px] bg-white/[0.02] border border-white/[0.04] transition-opacity ${disabled ? 'opacity-40' : ''}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="flex items-center gap-2 text-[13px] font-medium text-white/80">{icon}{label}</span>
      <span className="text-[14px] font-bold text-white tabular-nums drop-shadow-sm">{value}</span>
    </div>
    <input type="range" min={min} max={max} value={value} disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full h-1.5 rounded-full appearance-none bg-white/10 accent-primary cursor-pointer disabled:cursor-not-allowed shadow-inner" />
  </div>
)

export const ToggleRow: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = ({ label, checked, onChange }) => (
  <button onClick={() => onChange(!checked)} className="w-full flex items-center justify-between p-3 rounded-[12px] bg-white/[0.02] border border-white/[0.04] tap-highlight-transparent group hover:bg-white/[0.04] transition-colors">
    <span className="text-[13px] font-medium text-white/80 group-hover:text-white transition-colors">{label}</span>
    <div className={`w-10 h-6 rounded-full transition-colors flex items-center p-1 shadow-inner ${checked ? 'bg-primary' : 'bg-white/10'}`}>
      <div className={`w-4 h-4 rounded-full bg-white shadow-md transition-transform duration-300 ease-out ${checked ? 'translate-x-[16px]' : 'translate-x-0'}`} />
    </div>
  </button>
)

export const MiniStat: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-white/[0.04]">
    {icon}
    <div>
      <p className="text-[8px] text-white/25">{label}</p>
      <p className="text-[12px] font-bold text-text tabular-nums">{value}</p>
    </div>
  </div>
)

export function fmtTotal(m: number): string {
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  return m % 60 === 0 ? `${h}h` : `${h}h ${m % 60}m`
}
